import { Ref, WatchStopHandle } from 'vue';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import isPlainObject from 'lodash/isPlainObject';
import isNumber from 'lodash/isNumber';
import { LAYER_CONFIG_OMIT_PROP } from '../constants';

export default function useHeatLayer(mapInstance: LooseObject, layerInfo: Ref<MapLayer<HeatLayerConfig>>) {
  if (!layerInfo.value || !layerInfo.value.config || !TMap.visualization?.Heat) return;

  const options = omit(layerInfo.value.config, LAYER_CONFIG_OMIT_PROP);

  let unwatchList: WatchStopHandle[] | null = [];

  const heatOptions = {
    ...omit(options, 'zoomRange', 'gradientColor'),
    radius: isNumber(options.radius) ? options.radius : 50,
    minZoom: options.zoomRange?.[0],
    maxZoom: options.zoomRange?.[1],
    gradientColor: isPlainObject(options.gradientColor) && !isEmpty(options.gradientColor) ? new TMap.GradientColor({
      stops: options.gradientColor,
    }) : undefined,
  };

  const heatLayer = new TMap.visualization.Heat(heatOptions);
  heatLayer.addTo(mapInstance);

  unwatchList.push(watchEffect(() => {
    try {
      heatLayer?.setData(layerInfo.value.config.data);
    } catch (error) {
      heatLayer?.setData([]);
    }
  }));

  Object.keys(options).forEach((key) => {
    unwatchList?.push(watch(() => layerInfo.value.config[key], (newVal) => {
      if (key === 'enableAggregation') {
        heatLayer.unmount();
        useHeatLayer(mapInstance, layerInfo);
      } else {
        heatOptionsHandlers[key](newVal, heatLayer);
      }
    }, { deep: true }));
  });

  heatLayer.unmount = (): void => {
    heatLayer?.destroy();
    unwatchList?.forEach(unwatch => unwatch());
    unwatchList = null;
  };

  return heatLayer;
}

const heatOptionsHandlers = {
  radius(val: number, layerInstance: LooseObject): void {
    if (!isNumber(val)) {
      console.warn('请传入数字类型，当前传入的是：', val);
      return;
    }
    layerInstance.setRadius(val);
  },
  height(val: number, layerInstance: LooseObject): void {
    layerInstance.setHeight(val);
  },
  gradientColor(val: LooseObject, layerInstance: LooseObject): void {
    layerInstance.setGradientColor(new TMap.GradientColor({
      stops: val,
    }));
  },
  min(val: number, layerInstance: LooseObject): void {
    layerInstance.setMin(val);
  },
  max(val: number, layerInstance: LooseObject): void {
    layerInstance.setMax(val);
  },
  opacity(val: number, layerInstance: LooseObject): void {
    layerInstance.setOpacity(val);
  },
  zoomRange(val: HeatLayerConfig['zoomRange'], layerInstance: LooseObject): void {
    layerInstance.setMinZoom(val?.[0]);
    layerInstance.setMaxZoom(val?.[1]);
  },
};
